import React, { useState, useRef, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto'; 
// import ZoomPlugin from 'chartjs-plugin-zoom';

import TogglerSwitch from '../components/TogglerSwitch';
import { Select, MenuItem, Typography, FormControl, Grid, Button } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { MultiSelect } from "react-multi-select-component";
import appConfig from "../config/appConfig";
import { AutoScrollableChartContainer } from '../components/AutoScrollableChartContainer/AutoScrollableChartContainer';
import { useTranslation } from 'react-i18next';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  // ZoomPlugin
);

// import Paper from '@mui/material/Paper';
// import moment from 'moment';
const dropDownlistCss = { mt: 1, mb: 1, borderRadius: "5px", border: `1px solid ${appConfig.colors.darkblue}`, background: "#fcfcfb", width: "100%", '.MuiAutocomplete-input': { height: "5px" } };
const gridItemCss = { '&.MuiGrid-item': { pt: 0, pl: 2, pr: 2} };

const ChartSwitcher = ({ 
  chartData, 
  id,
  title, 
  type, 
  tableData, 
  filterDropdown, 
  handleFilterChange, 
  yearData, 
  filterList,
  handleLocationChange,
  selectedLocationNames,
  selectedBusinessUnits,
  handleBusinessUnitChange,
  selectedCountryNames,
  handleCountryChange,
  countryList = null,
  onExportReport,
}) => {
  const chartRef = useRef(null);
  const { t } = useTranslation('common');

  useEffect(() => {
    const chartInstance = chartRef.current?.chartInstance;

    if (chartInstance) {
      const handleWheel = (event) => {
        if (event.deltaX !== 0) {
          event.preventDefault();
          const currentMax = chartInstance.scales['x-axis-0'].max;
          const currentMin = chartInstance.scales['x-axis-0'].min;
          const newMax = currentMax + (event.deltaX > 0 ? 1 : -1);
          const newMin = currentMin + (event.deltaX > 0 ? 1 : -1);

          chartInstance.scales['x-axis-0'].max = newMax;
          chartInstance.scales['x-axis-0'].min = newMin;
          chartInstance.update();
        }
      };

      chartInstance.canvas.addEventListener('wheel', handleWheel);

      return () => {
        chartInstance.canvas.removeEventListener('wheel', handleWheel);
      };
    }
  }, []);

  const [activeComponent, setActiveComponent] = useState('chart');
  const handleDivActivation = (divName) => {
    setActiveComponent(divName);
  };
  const handleChange = (e) => {
    const value = e.target.value;
    handleFilterChange(value); // Pass the selected value to the parent component
  };

  const getSelectedLocations = () => {
    return filterList?.flatMap(item => item?.businessLocation?.map((location) => ({
      "locationId": location?.id,
      "locationName": location?.locationName
    })));
  };
  const updateLocationOptions = () => {
    const locations = getSelectedLocations();
    const locationOptions = [...new Set(locations)]?.map((item) => ({
      label: item?.locationName,
      value: item?.locationId,
    })).sort((a, b) => a?.label?.localeCompare(b?.label));;
    const businessUnitOptions = filterList?.map((businessUnit) => ({
      label: businessUnit?.businessUnitName,
      value: businessUnit?.businessUnitId,
    }));
    return locationOptions;
  };
  const locationOptions = updateLocationOptions();

  const businessUnitOptions = filterList
  ?.map((businessUnit) => ({
    label: businessUnit.businessUnitName,
    value: businessUnit.businessUnitId,
  }))
  .sort((a, b) => a?.label?.localeCompare(b?.label));

  const defaultFilter = businessUnitOptions?.map(item => item?.value);


  const horizontalStackedBarOption = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        bottom: 20, // Adjust as needed
      },
      height: {
        max: chartData?.height?.max, // Set the maximum height
        min:  chartData?.height?.min, // Set the minimum height
      },
    },
    scales: {
      x: {
        ticks: {
          callback: function (value) {
            return '$' + Intl.NumberFormat().format(Number(value));
          },
            font: {
                size: 14,
            }
        },
        stacked: true,
        border: {
          dash: [4,4],
      },
      },
      y: {
        stacked: true,
        ticks: {
          font: {
              size: 14,
          }
        },
        grid: {
          drawBorder: false, // Disable the grid border
          drawOnChartArea: false, // Only draw the grid on the chart area
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        title: { display: true, padding: 14 },
        onHover: (event) => {
          event.native.target.style.cursor = 'pointer'
        },
        onLeave: function (e) {
          e.native.target.style.cursor = "default";
        },
        labels: {
            usePointStyle: true,
            font: {
              size: 14
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const datasetLabel = context.dataset.label || '';
            return datasetLabel + ': $ ' + context.formattedValue;
          },
        },
      },
    },
  };
  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      // padding: {
      //   bottom: 20, // Adjust as needed
      // },
    },
    scales: {
      x: {
        type: 'category', // Use 'category' type for string labels on x-axis
        grid: {
          drawBorder: false, // Disable the grid border
          drawOnChartArea: false, // Only draw the grid on the chart area
        },
        ticks: {
          stepSize: 1, // Set the step size between ticks on the x-axis
          autoSkip: false,
          maxRotation: 45, // Rotate labels at a 45-degree angle
          minRotation: 55, // Set a minimum rotation angle
          font: {
            size: 14,
          },
        },
       
      },
      y: {
        type: 'logarithmic', // Use a logarithmic scale for the y-axis
        min: 1,
        max: 350000,
        border: {
          dash: [4, 4],
        },
        ticks: {
          // stepSize: 50,
          callback: function (value, index, values) {
            if (index % 2 === 0) {
              return '$' + Intl.NumberFormat().format(Number(value));
            } else {
              return ''; // Hide labels for odd indices
            }
  
          },
          font: {
            size: 14,
          },
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        title: { display: true, padding: 14 },
        position: 'bottom',
        onHover: (event) => {
          event.native.target.style.cursor = 'pointer';
        },
        onLeave: function (e) {
          e.native.target.style.cursor = 'default';
        },
        labels: {
          usePointStyle: true,
          font: {
            size: 14,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const datasetLabel = context.dataset.label || '';
            return datasetLabel + ': $ ' + context.formattedValue;
          },
        },
      },
    },
  };
  const barChartOptions2 = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        bottom: 20, // Adjust as needed
      },
    },
    scales: {
      x: {
        type: 'category', // Use 'category' type for string labels on x-axis
        grid: {
          drawBorder: false, // Disable the grid border
          drawOnChartArea: false, // Only draw the grid on the chart area
        },
        ticks: {
          // stepSize: 1, // Set the step size between ticks on the x-axis
          autoSkip: false,
          // maxRotation: 45, // Rotate labels at a 45-degree angle
          // minRotation: 55, // Set a minimum rotation angle
          font: {
            size: 14,
          },
        },
       
      },
      y: {
        type: 'logarithmic', // Use a logarithmic scale for the y-axis
        min: 1,
        max: 350000,
        border: {
          dash: [4, 4],
        },
        ticks: {
          // stepSize: 50,
          callback: function (value, index, values) {
            if (index % 2 === 0) {
              return '$' + Intl.NumberFormat().format(Number(value));
            } else {
              return ''; // Hide labels for odd indices
            }
            // return '$' + Intl.NumberFormat().format(Number(value));
          },
          font: {
            size: 14,
          },
        },
      },
    },
    plugins: {
      legend: {
        display: true,
        title: { display: true, padding: 14 },
        position: 'bottom',
        onHover: (event) => {
          event.native.target.style.cursor = 'pointer';
        },
        onLeave: function (e) {
          e.native.target.style.cursor = 'default';
        },
        labels: {
          usePointStyle: true,
          font: {
            size: 14,
          },
        },
      },
    //   zoom: {
    //     pan: {
    //         enabled: true,
    //         mode: 'x'
    //     },
    //     zoom: {
    //         pinch: {
    //             enabled: true       // Enable pinch zooming
    //         },
    //         wheel: {
    //             enabled: true       // Enable wheel zooming
    //         },
    //         mode: 'x',
    //     }
    // },
      tooltip: {
        callbacks: {
          label: function (context) {
            const datasetLabel = context.dataset.label || '';
            return datasetLabel + ': $ ' + context.formattedValue;
          },
        },
      },
    },
  };
  
  const TableView = () => (
    <TableContainer className='dash-table' style={{height: 450}}>
      <Table aria-label="simple table">
        <TableHead className='kpi-table kpi-table-th'>
          <TableRow>
          {tableData?.data?.header?.map((label, index) => (
            <TableCell>{label}</TableCell>
          ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {tableData?.data?.body?.map((row, index) => (
            <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
            {Object.keys(row).map((key) => (
              <TableCell key={key} align="center">
                {row[key]}
              </TableCell>
            ))}
          </TableRow>
        
          ))}
        </TableBody>
      </Table>
    </TableContainer>


    // <div>
    //   <table>
    //     <thead>
    //       <tr>
    //       {tableData?.header?.map((label, index) => (
    //         <th>{label}</th>
    //       ))}
    //       </tr>
    //     </thead>
    //     <tbody>
    //       {tableData.body.map((label, index) => (
    //         <tr key={index}>
    //           <td>{label.country}</td>
    //           <td>{label.martinreaFunds}</td>
    //           <td>{label.employeeFunds}</td>
    //           <td>{label.volunteerHour}</td>
    //         </tr>
    //       ))}
    //     </tbody>
    //   </table>
    // </div>
  );


  return (
    
    <div>
      <div className='filter-holder-header'>
      <Typography className='md-bold'>{title}</Typography>
      <div className='filters'>
      
        <TogglerSwitch onDivActivate={handleDivActivation} />
        {activeComponent === 'table' && (
          <Button className='xxxs-normal export-btn' style={{margin: "0px 0  10px 0px"}} onClick={onExportReport} variant="outlined">{t("Export")}</Button>
        )}
        <Select
            value={filterDropdown}
            onChange={handleChange}
            style={{margin: "0px 10px  10px 0px"}}
            className='filter-dropdown xxxs-normal'
            inputProps={{ 'aria-label': 'Without label' }}
          >
            {/* <MenuItem className='xxxs-normal filter-item' value={'week'}>This week</MenuItem>
            <MenuItem className='xxxs-normal filter-item' value={'month'}>This month</MenuItem> */}
            <MenuItem className='xxxs-normal filter-item' value={'ALL'}>{t("All")}</MenuItem>
            {yearData?.map((item, i) => (
              <MenuItem key={i} className='xxs-normal filter-item' value={item}>
                  {item}
              </MenuItem>
              ))}
            {/* <MenuItem className='xxxs-normal filter-item' value={moment().startOf('year')?.format("YYYY")}>This Year</MenuItem> */}
          </Select>
          {filterList && filterList?.length > 0 &&  (
          <div className='dash-loc' style={{width: '185px'}} sx={gridItemCss}>
            {id === "businessUnitView" && (
                <FormControl sx={dropDownlistCss}>
                <MultiSelect
                  options={businessUnitOptions}
                  value={selectedBusinessUnits}
                  onChange={handleBusinessUnitChange}
                  labelledBy="Select"
                  overrideStrings={{
                    "allItemsAreSelected": t("All items are selected."),
                    "noOptions": t("No options"),
                    "search": t("Search"),
                    "selectAll": t("Select All"),
                    "selectSomeItems": t("Select...")
                  }}
                />
                </FormControl>
            )}
            {id === "regionalView" && (
                <FormControl sx={dropDownlistCss}>
                <MultiSelect
                  options={countryList}
                  value={selectedCountryNames}
                  onChange={handleCountryChange}
                  labelledBy="Select"
                  overrideStrings={{
                    "allItemsAreSelected": t("All items are selected."),
                    "noOptions": t("No options"),
                    "search": t("Search"),
                    "selectAll": t("Select All"),
                    "selectSomeItems": t("Select...")
                  }}
                />
                </FormControl>
            )}
            </div>
           )}
           {filterList && filterList?.length > 0 &&  id !== "businessUnitView" &&  id !== "regionalView" && (
          <div className='dash-loc' style={{width: '185px'}} sx={gridItemCss}>
            <FormControl sx={dropDownlistCss}>
            <MultiSelect
              options={locationOptions}
              value={selectedLocationNames}
              onChange={handleLocationChange}
              labelledBy="Select"
              overrideStrings={{
                "allItemsAreSelected": t("All items are selected."),
                "noOptions": t("No options"),
                "search": t("Search"),
                "selectAll": t("Select All"),
                "selectSomeItems": t("Select...")
              }}
            />
            </FormControl>
            </div>
           )}
          
        </div>
        </div>
      {activeComponent === 'chart' && (
          <AutoScrollableChartContainer data={chartData?.data} chartRef={chartRef} minHeight={chartData?.height?.min}>
              <Bar ref={chartRef}  data={chartData?.data} options={type === 'stackedBar' ? horizontalStackedBarOption : type === 'zoom-view' ? barChartOptions2 : barChartOptions} />
          </AutoScrollableChartContainer>
      )}
      {activeComponent === 'table' && (
         <div style={{height: 450, width: '100%',position: 'relative',zIndex: 0}}>
        <TableView />
        </div>
      )}
    </div>
  );
};

export default ChartSwitcher;
